/**
 * Frontend Permission Utilities
 * 
 * Utilities for checking user permissions in the frontend.
 * Uses the shared permission system for consistent RBAC enforcement.
 */

import { 
  ACTIONS, 
  RESOURCES, 
  SCOPES, 
  PERMISSION_MATRIX,
  hasPermission
} from './rbacPermissions';

/**
 * Check if user has organization-wide access to a resource
 * @param {string} userRole - The user's role
 * @param {string} resource - The resource to check (e.g., 'student')
 * @param {string} action - The action to check (e.g., 'read')
 * @param {Object} context - User context (organization_id, etc.)
 * @returns {boolean} - Whether user has organization-wide access
 */
export function hasOrganizationWideAccess(userRole, resource, action, context = {}) {
  try {
    // First check if user has permission at all
    const hasBasicPermission = hasPermission(userRole, resource, action, context);
    
    if (!hasBasicPermission) {
      return false;
    }

    // Get the permission matrix for this role to check the actual scope
    const rolePermissions = PERMISSION_MATRIX[userRole];
    if (!rolePermissions) {
      return false;
    }

    const resourcePermissions = rolePermissions[resource];
    if (!resourcePermissions) {
      return false;
    }

    const actionScope = resourcePermissions[action];
    if (!actionScope) {
      return false;
    }

    // Check if the permission scope is organization-wide
    const isOrganizationWide = actionScope === SCOPES.ORGANIZATION;
    
    return isOrganizationWide;
  } catch (error) {
    console.error('❌ Error checking organization-wide access:', error);
    return false;
  }
}

/**
 * Check if user can access Student Management page (organization-wide student access)
 * @param {string} userRole - The user's role
 * @param {Object} context - User context (organization_id, etc.)
 * @returns {boolean} - Whether user can access Student Management
 */
export function canAccessStudentManagement(userRole, context = {}) {
  return hasOrganizationWideAccess(userRole, RESOURCES.STUDENT, ACTIONS.READ, context);
}

/**
 * Get permission-based navigation items
 * Filters navigation items based on user permissions
 * @param {Array} navigationItems - Array of navigation items
 * @param {string} userRole - The user's role  
 * @param {Object} context - User context
 * @param {Function} permissionChecker - Custom permission checker function
 * @returns {Array} - Filtered navigation items
 */
export function filterNavigationByPermissions(navigationItems, userRole, context, permissionChecker) {
  if (!userRole || !permissionChecker) {
    return navigationItems;
  }

  return navigationItems.filter(item => {
    if (item.requiresPermission) {
      const { resource, action } = item.requiresPermission;
      return permissionChecker(resource, action, context);
    }
    return true;
  });
}

/**
 * Frontend Permission Guard Hook
 * React hook for permission-based rendering
 * @param {Object} user - Current user object with role and context
 * @returns {Object} Permission checking utilities
 */
export function usePermissionGuard(user) {
  const context = {
    organization_id: user?.organization_id,
    schoolId: user?.schoolId,
    userId: user?.id
  };

  return {
    hasPermission: (resource, action, additionalContext = {}) => {
      if (!user?.role) return false;
      return hasPermission(user.role, resource, action, { ...context, ...additionalContext });
    },
    
    hasOrganizationAccess: (resource, action, additionalContext = {}) => {
      if (!user?.role) return false;
      return hasOrganizationWideAccess(user.role, resource, action, { ...context, ...additionalContext });
    },
    
    canAccessStudentManagement: () => {
      if (!user?.role) return false;
      return canAccessStudentManagement(user.role, context);
    },

    // Role-based shortcuts
    isTenantAdmin: () => user?.role === 'tenant_administrator',
    isOrgAdmin: () => user?.role === 'organization_admin', 
    isOrgTeacher: () => user?.role === 'organization_teacher',
    isOrgStaff: () => user?.role === 'organization_staff',
    isSchoolAdmin: () => user?.role === 'school_admin',
    isTeacher: () => user?.role === 'teacher',

    // Scope-based shortcuts  
    hasOrganizationScope: () => {
      const orgRoles = ['tenant_administrator', 'organization_admin', 'organization_teacher', 'organization_staff'];
      return orgRoles.includes(user?.role);
    },

    hasSchoolScope: () => {
      const schoolRoles = ['school_admin', 'teacher', 'substitute', 'support_staff'];
      return schoolRoles.includes(user?.role);
    }
  };
}

export { ACTIONS, RESOURCES, SCOPES };